import { HttpClient, HttpContext } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { environment } from '../environments/test.environment';
import {
  ApplicationSessionResponse,
  CustomerCatalogResponse,
  LegacyLoginResponse,
  LoginRequest,
  StoredAuthSession
} from '../models/auth.models';
import { SKIP_DSDN_AUTH } from '../interceptors/auth-http-context';

const SESSION_STORAGE_KEY = 'dsdn_auth_session';
const ROLE_CLAIMS = [
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
  'role',
  'roles'
] as const;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apiUrl = `${environment.apiBaseUrl}/api`;
  private readonly browser: boolean;
  private readonly sessionSubject = new BehaviorSubject<StoredAuthSession | null>(null);
  private readonly rolesSubject = new BehaviorSubject<string[]>([]);
  private readonly profilePhotoSubject = new BehaviorSubject<string | null>(null);
  private readonly companyLogoSubject = new BehaviorSubject<string | null>(null);

  readonly session$ = this.sessionSubject.asObservable();
  readonly profilePhoto$ = this.profilePhotoSubject.asObservable();
  readonly companyLogo$ = this.companyLogoSubject.asObservable();

  private roles: string[] = [];

  constructor(
    private readonly http: HttpClient,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.browser = isPlatformBrowser(platformId);
    this.restoreSession();
  }

  login(request: LoginRequest): Observable<LegacyLoginResponse> {
    return this.http.post<LegacyLoginResponse>(`${this.apiUrl}/auth/login`, request).pipe(
      tap(response => this.applyLegacyLogin(response))
    );
  }

  exchangeMicrosoftToken(idToken: string): Observable<ApplicationSessionResponse> {
    return this.http
      .post<ApplicationSessionResponse>(
        `${this.apiUrl}/auth/sso/microsoft`,
        { idToken },
        { context: new HttpContext().set(SKIP_DSDN_AUTH, true) }
      )
      .pipe(tap(response => this.applySession(response)));
  }

  getAvailableCustomers(): Observable<CustomerCatalogResponse> {
    return this.http.get<CustomerCatalogResponse>(`${this.apiUrl}/customers/available`);
  }

  selectCustomer(customerId: string): Observable<ApplicationSessionResponse> {
    return this.http
      .post<ApplicationSessionResponse>(`${this.apiUrl}/auth/customer-context`, { customerId })
      .pipe(tap(response => this.applySession(response)));
  }

  logout(): void {
    if (this.browser) {
      [
        SESSION_STORAGE_KEY,
        'auth_token',
        'user_name',
        'company',
        'id',
        'companyId',
        'Firstname',
        'Lastname',
        'selectedProject'
      ].forEach(key => localStorage.removeItem(key));
    }

    this.sessionSubject.next(null);
    this.roles = [];
    this.rolesSubject.next([]);
    this.profilePhotoSubject.next(null);
    this.companyLogoSubject.next(null);
  }

  isAuthenticated(): Observable<boolean> {
    this.expireInvalidSession();
    return this.sessionSubject.pipe(map(session => session !== null));
  }

  hasCustomerContext(): boolean {
    const session = this.getSession();
    return session?.sessionType === 'customer'
      && !session.customerSelectionRequired
      && Boolean(session.customer?.id);
  }

  isReadOnly(): boolean {
    return this.getSession()?.accessLevel === 'readOnly';
  }

  isMicrosoftSession(): boolean {
    return this.getSession()?.authSource === 'microsoft';
  }

  canSwitchCustomer(): boolean {
    return this.getSession()?.canSwitchCustomer === true;
  }

  getSession(): StoredAuthSession | null {
    this.expireInvalidSession();
    return this.sessionSubject.value;
  }

  getToken(): string | null {
    return this.getSession()?.accessToken ?? null;
  }

  getUserName(): string | null {
    return this.getSession()?.user.userName ?? null;
  }

  getUserNameObservable(): Observable<string | null> {
    return this.sessionSubject.pipe(map(session => session?.user.userName ?? null));
  }

  getCompany(): string | null {
    return this.getSession()?.customer?.name ?? null;
  }

  getCompanyObservable(): Observable<string | null> {
    return this.sessionSubject.pipe(map(session => session?.customer?.name ?? null));
  }

  getUserId(): string | null {
    return this.getSession()?.user.id ?? null;
  }

  getCompanyId(): string | null {
    return this.getSession()?.customer?.id ?? null;
  }

  getRoles(): string[] {
    return [...this.roles];
  }

  getRolesObservable(): Observable<string[]> {
    return this.rolesSubject.asObservable();
  }

  setRoles(roles: string[]): void {
    this.roles = [...roles];
    this.rolesSubject.next([...roles]);
  }

  hasRole(role: string): boolean {
    const normalizedRole = role.trim().toLowerCase();
    return this.roles.some(
      assignedRole => assignedRole.trim().toLowerCase() === normalizedRole
    );
  }

  hasAnyRole(roles: string[]): boolean {
    const assignedRoles = new Set(
      this.roles.map(role => role.trim().toLowerCase())
    );
    return roles.some(role => assignedRoles.has(role.trim().toLowerCase()));
  }

  setProfilePhoto(photoUrl: string | null): void {
    this.profilePhotoSubject.next(photoUrl);
  }

  setCompanyLogo(logoUrl: string | null): void {
    this.companyLogoSubject.next(logoUrl);
  }

  getCompanyLogo(): string | null {
    return this.companyLogoSubject.value;
  }

  private applyLegacyLogin(response: LegacyLoginResponse): void {
    const session: StoredAuthSession = {
      accessToken: response.token,
      expiresAtUtc: this.getTokenExpiration(response.token),
      sessionType: 'customer',
      customerSelectionRequired: false,
      canSwitchCustomer: false,
      accessLevel: 'standard',
      authSource: 'password',
      user: {
        id: response.id,
        userName: response.userName,
        email: response.email?.trim() || null,
        firstName: response.firstName,
        lastName: response.lastName,
        roles: this.getRolesFromToken(response.token)
      },
      customer: {
        id: response.companyId,
        name: response.company,
        logoUrl: null
      }
    };

    this.applySession(session);
  }

  private applySession(response: ApplicationSessionResponse): void {
    const previousCustomerId = this.sessionSubject.value?.customer?.id ?? null;
    const session: StoredAuthSession = {
      ...response,
      user: {
        ...response.user,
        roles: response.user.roles?.length
          ? [...response.user.roles]
          : this.getRolesFromToken(response.accessToken)
      },
      customer: response.customer ? { ...response.customer } : undefined
    };

    const nextCustomerId = session.customer?.id ?? null;
    if (this.browser && nextCustomerId && nextCustomerId !== previousCustomerId) {
      // Customer-specific view state must never carry into another customer's workspace.
      localStorage.removeItem('selectedProject');
      localStorage.removeItem('request-page-filter-state');
    }

    this.sessionSubject.next(session);
    this.setRoles(session.user.roles);
    this.companyLogoSubject.next(session.customer?.logoUrl ?? null);
    this.persistSession(session);
  }

  private persistSession(session: StoredAuthSession): void {
    if (!this.browser) return;

    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    localStorage.setItem('auth_token', session.accessToken);
    localStorage.setItem('user_name', session.user.userName);
    localStorage.setItem('id', session.user.id);
    localStorage.setItem('Firstname', session.user.firstName);
    localStorage.setItem('Lastname', session.user.lastName);

    if (session.customer) {
      localStorage.setItem('company', session.customer.name);
      localStorage.setItem('companyId', session.customer.id);
    } else {
      localStorage.removeItem('company');
      localStorage.removeItem('companyId');
    }
  }

  private restoreSession(): void {
    if (!this.browser) return;

    const serializedSession = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!serializedSession) {
      this.restoreLegacySession();
      return;
    }

    try {
      const session = JSON.parse(serializedSession) as StoredAuthSession;
      if (!this.isTokenExpired(session.accessToken)) {
        this.sessionSubject.next(session);
        this.setRoles(
          session.user?.roles?.length
            ? session.user.roles
            : this.getRolesFromToken(session.accessToken)
        );
        return;
      }
    } catch {
      // Invalid persisted state is cleared below and cannot be trusted.
    }

    this.logout();
  }

  private restoreLegacySession(): void {
    const token = localStorage.getItem('auth_token');
    const companyId = localStorage.getItem('companyId');
    const company = localStorage.getItem('company');
    const userId = localStorage.getItem('id');
    const userName = localStorage.getItem('user_name');

    if (!token || !companyId || !company || !userId || !userName || this.isTokenExpired(token)) {
      return;
    }

    this.applySession({
      accessToken: token,
      expiresAtUtc: this.getTokenExpiration(token),
      sessionType: 'customer',
      customerSelectionRequired: false,
      canSwitchCustomer: false,
      accessLevel: 'standard',
      authSource: 'password',
      user: {
        id: userId,
        userName,
        firstName: localStorage.getItem('Firstname') ?? '',
        lastName: localStorage.getItem('Lastname') ?? '',
        roles: this.getRolesFromToken(token)
      },
      customer: {
        id: companyId,
        name: company,
        logoUrl: null
      }
    });
  }

  private expireInvalidSession(): void {
    const session = this.sessionSubject.value;
    if (session && this.isTokenExpired(session.accessToken)) {
      this.logout();
    }
  }

  private isTokenExpired(token: string): boolean {
    try {
      const decoded = jwtDecode<{ exp?: number }>(token);
      return !decoded.exp || decoded.exp * 1000 <= Date.now();
    } catch {
      return true;
    }
  }

  private getTokenExpiration(token: string): string {
    try {
      const decoded = jwtDecode<{ exp?: number }>(token);
      if (decoded.exp) {
        return new Date(decoded.exp * 1000).toISOString();
      }
    } catch {
      // The existing login endpoint remains authoritative for token validity.
    }

    return new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString();
  }

  private getRolesFromToken(token: string): string[] {
    try {
      const decoded = jwtDecode<Record<string, unknown>>(token);
      const roles = ROLE_CLAIMS.flatMap(claimName => {
        const claim = decoded[claimName];
        if (Array.isArray(claim)) {
          return claim.filter((role): role is string => typeof role === 'string');
        }
        return typeof claim === 'string' ? [claim] : [];
      });

      return roles.filter((role, index) =>
        role.trim().length > 0
        && roles.findIndex(candidate =>
          candidate.trim().toLowerCase() === role.trim().toLowerCase()
        ) === index
      );
    } catch {
      return [];
    }
  }

}
